/**
 * ``useAlertNotifications`` — raises the Flow-F notification for an
 * arriving SAPIENT alert, and retracts it when the alert is handled
 * (ADR-029 §5; REQ-UI-062).
 *
 * This is the dispatch the ``TODO(flow-f)`` hook in
 * :func:`alerts/use-alerts-sse-bridge` was holding open. It lives in its own
 * hook rather than inside that bridge deliberately: the bridge is a pure
 * store-warmer that unit-tests without any provider, whereas raising a
 * notification needs React context (the dismiss mutation, the session role,
 * the selection bus). Keeping them separate keeps the bridge's simplicity
 * and gives this concern its own tests.
 *
 * Both are mounted together in :func:`map/use-map-sse-bridges`.
 *
 * # Two directions
 *
 *   - **``alert``** — raise the mapped surface, subject to the operator's
 *     threshold (CRITICAL bypasses it).
 *   - **``alert-dismissed``** — the cross-session fan-out (ADR-029 [MVP]
 *     item 6). When *any* operator dismisses an alert, every session
 *     retracts its live notification for it, so a handled alert stops
 *     interrupting everyone at once. This is a **local close** — the
 *     dismissal already happened server-side; re-invoking Dismiss here
 *     would POST a redundant dismissal per connected session.
 *
 * Cross-references:
 *   - ADR-029 §5 / §6.1 / §7 / §9 / [MVP] item 6.
 *   - REQ-UI-062; REQ-QUERY-025; storyboard Flow F Frame 3 (SAPIENT variant).
 *   - alerts/alert-notification.ts (the pure copy + threshold logic).
 */
import { useRef } from "react";
import {
  DEFAULT_ALERT_NOTIFICATION_THRESHOLD,
  type AlertNotificationThreshold,
  alertNotificationKey,
  buildAlertNotification,
  shouldNotifyForAlert,
} from "@/alerts/alert-notification";
import { ALERT_DISMISSER_ROLES } from "@/alerts/alert-display";
import { alertRecordFromSse } from "@/alerts/use-alerts-sse-bridge";
import { useDismissAlert } from "@/alerts/use-dismiss-alert";
import { useSession } from "@/auth/session-context";
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import { useCloseNotification, useNotify } from "@/notifications/use-notify";
import { useSelectionStore } from "@/selection/selection-store";

/** Options for {@link useAlertNotifications}. */
export interface AlertNotificationOptions {
  /**
   * The operator's notification threshold (ADR-029 §6.1). Defaults to
   * :data:`alert-notification.DEFAULT_ALERT_NOTIFICATION_THRESHOLD`
   * (``all``).
   *
   * Phase 4 sources this from the persisted preference; until then every
   * caller gets the ratified default, which reproduces the deterministic
   * ADR-029 §5 map exactly.
   */
  readonly threshold?: AlertNotificationThreshold;
}

/**
 * Mount the alert → notification dispatch against the Map's live
 * ``sseMessage``. Pure side-effect; returns nothing.
 *
 * # Why this consumer forced the plumbing fix (issue #739)
 *
 * Every other consumer of the SSE feed writes to a store the REST poll also
 * fills, so a dropped live message cost latency and nothing else. This one
 * has **no backfill**: the alert still reaches the Alerts panel via the
 * poll, but the *interruption* — the toast or the CRITICAL banner — happens
 * once or never. While the feeder was a single-slot ``lastMessage``, an
 * alert burst raised strictly fewer notifications than there were alerts,
 * worst exactly when it matters most (the flood conditions ADR-029 §10
 * exists to manage). Subscribing means one notification decision per alert,
 * always.
 *
 * @param subscribe - The Map's ``connection.subscribe``. ``null`` mounts no
 *     subscription (no connection yet).
 * @param opts - Threshold override (see :type:`AlertNotificationOptions`).
 */
export function useAlertNotifications(
  subscribe: SseSubscribe | null,
  opts: AlertNotificationOptions = {},
): void {
  const threshold = opts.threshold ?? DEFAULT_ALERT_NOTIFICATION_THRESHOLD;
  const notify = useNotify();
  const { closeByDedupeKey } = useCloseNotification();
  const dismiss = useDismissAlert();
  const canDismiss = useSession().hasAnyRole(ALERT_DISMISSER_ROLES);
  const inspect = useSelectionStore((s) => s.inspect);

  // The mutation object is a fresh reference each render; hold it in a ref so
  // the handler below reaches the current mutation without the identity churn
  // mattering. (:func:`useSseMessages` already keeps the handler itself
  // ref-fresh, so this is about the mutation object, not the subscription.)
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useSseMessages(subscribe, (message) => {
    if (message.kind === "alert") {
      const alert = alertRecordFromSse(message.payload);
      if (!shouldNotifyForAlert(alert.priority, threshold)) {
        return;
      }
      notify(
        buildAlertNotification(alert, {
          // Opens the Entity Details panel on the alert (Flow F Frame 3 —
          // "a 'View →' opening the alert detail panel").
          onView: () => inspect({ kind: "alert", id: alert.alert_id }),
          // Hidden for roles without the alerts.dismiss capability, matching
          // the Alerts panel's fail-safe UX (ADR-029 §9); the BFF enforces
          // the same gate independently.
          ...(canDismiss
            ? { onDismiss: () => dismissRef.current.mutate({ alertId: alert.alert_id }) }
            : {}),
        }),
      );
    } else if (message.kind === "alert-dismissed") {
      // Handled elsewhere — retract the overlay locally, POST nothing.
      closeByDedupeKey(alertNotificationKey(message.payload.alert_id));
    }
  });
}
